import { useMemo } from 'react';
import { generatePath } from 'react-router-dom';
import dayjs from 'dayjs';
import { useAppQuery } from 'services/reactQuery/useAppQuery';
import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { useJournalId } from 'hooks/useJournalId/useJournalId';
import { DayAttendanceDisplay, JournalAttendanceFilters } from 'components/molecules';
import {
    AppColumnsType,
    AppColumnType,
    Attendance,
    GroupDayAttendanceDTO,
    JournalChildMonthlyGroupAttendanceDTO,
} from 'types';
import { getFullName } from 'utils/getFullName';
import { childMonthlyAttendanceDTOFields } from 'consts/attendance/childMonthlyAttendanceDTOFields';
import { appPaths } from 'consts/paths/paths';
import { useDateFilter } from 'hooks/useDateFilter/useDateFilter';
import { DATE_FILTER_KEY } from 'consts/filters/common/filtersKeys';
import { parseStringIntoDayjsDate } from 'utils/parsers/dateTime/parseStringIntoDayjsDate';
import { FULL_MONTH_FORMAT } from 'consts/dateFormats';
import { useFirstAndLastJournalSchoolYears } from 'hooks/useFirstAndLastJournalSchoolYears/useFirstAndLastJournalSchoolYears';
import { EMPTY_VALUE_LABEL } from 'consts/labels/common';
import { commonTexts, journalTexts } from 'consts/text';
import { getMonthFromDayjsMonth } from 'utils/getMonthFromDayjsMonth';
import { useJournalDetailsBreadcrumb } from 'pages/app/staff/journal/journalDetails/hooks';

import { useNavigateWithState } from 'hooks/useNavigateWithState/useNavigateWithState';
import {
    FiltersContainer,
    StyledSummaryColumnTitle,
    StyledTable,
} from './GroupJournalMonthlyAttendancePage.styled';
import { MonthlyAttendanceTableHeader } from '../../../common/attendance/components';

const childrenColumn: AppColumnType<JournalChildMonthlyGroupAttendanceDTO> = {
    title: commonTexts.dataLabels.children,
    dataIndex: childMonthlyAttendanceDTOFields.child,
    key: childMonthlyAttendanceDTOFields.child,
    render: (child: JournalChildMonthlyGroupAttendanceDTO['child']) =>
        getFullName(child.firstName, child.lastName),
    fixed: 'left',
    mobileWidth: '100px',
};

const summaryColumns: AppColumnsType<JournalChildMonthlyGroupAttendanceDTO> = [
    {
        title: (
            <StyledSummaryColumnTitle>
                {journalTexts.journalDetails.common.attendance.presentShort}
            </StyledSummaryColumnTitle>
        ),
        dataIndex: childMonthlyAttendanceDTOFields.presences,
        key: childMonthlyAttendanceDTOFields.presences,
        align: 'center',
    },
    {
        title: (
            <StyledSummaryColumnTitle>
                {journalTexts.journalDetails.common.attendance.absentShort}
            </StyledSummaryColumnTitle>
        ),
        dataIndex: childMonthlyAttendanceDTOFields.absences,
        key: childMonthlyAttendanceDTOFields.absences,
        align: 'center',
    },
];

const MONTHLY_ATTENDANCE_TABLE_WIDTH = 1600;
const FREE_DAY_COLUMN_CLASS_NAME = 'free-day-column';

export const GroupJournalMonthlyAttendancePage = () => {
    const { journalId } = useJournalId();
    const { dateFilterValue, handleChangeDateFilter } = useDateFilter({
        filterKey: DATE_FILTER_KEY,
    });
    const { statefulNavigate } = useNavigateWithState();

    useJournalDetailsBreadcrumb({
        journalId,
        journalType: 'GROUP',
    });

    const selectedDate = dateFilterValue ? parseStringIntoDayjsDate(dateFilterValue) : dayjs();
    const selectedYear = selectedDate.year();
    const selectedMonth = getMonthFromDayjsMonth(selectedDate.month());
    const selectedFullMonth = selectedDate.format(FULL_MONTH_FORMAT);
    const currentAttendanceType: Attendance = 'MONTHLY';

    const { firstAndLastJournalSchoolYears, isJournalDataFetching, isJournalDataError } =
        useFirstAndLastJournalSchoolYears({ journalId });
    const {
        data: monthlyAttendanceData,
        isInitialLoading: isMonthlyAttendanceDataInitialLoading,
        isRefetching: isMonthlyAttendanceDataRefetching,
        isError: isMonthlyAttendanceDataError,
    } = useAppQuery('MONTHLY_ATTENDANCE', [journalId, selectedYear, selectedMonth], () =>
        StaffHTTPService.groupAttendance.getGroupMonthlyAttendance(
            journalId,
            selectedYear,
            selectedMonth,
        ),
    );

    const handleAttendanceTypeChange = (selectedAttendanceType: Attendance) => {
        if (selectedAttendanceType === currentAttendanceType) {
            return;
        }
        statefulNavigate(
            generatePath(
                appPaths.app.journals.groupJournalDetails.pickedJournal.attendance.daily.base,
                { journalId },
            ),
        );
    };

    const columns = useMemo(() => {
        const daysInMonth = selectedDate.daysInMonth();
        const daysNumbersArray = Array.from({ length: daysInMonth }, (_, index) => index + 1);
        const freeDays = monthlyAttendanceData?.freeDays || [];
        const dayAttendanceColumns = daysNumbersArray.reduce<
            AppColumnsType<JournalChildMonthlyGroupAttendanceDTO>
        >((allColumns, currentDayNumber) => {
            const columnTitle = currentDayNumber <= 9 ? `0${currentDayNumber}` : currentDayNumber;
            const isFreeDay = freeDays.includes(currentDayNumber);
            const column: AppColumnType<JournalChildMonthlyGroupAttendanceDTO> = {
                title: columnTitle,
                dataIndex: [childMonthlyAttendanceDTOFields.monthlyAttendance, currentDayNumber],
                key: currentDayNumber,
                className: isFreeDay ? FREE_DAY_COLUMN_CLASS_NAME : undefined,
                onHeaderCell: () => ({
                    className: isFreeDay ? FREE_DAY_COLUMN_CLASS_NAME : undefined,
                }),
                render: (dayAttendance?: GroupDayAttendanceDTO) => (
                    <DayAttendanceDisplay dayAttendance={dayAttendance} />
                ),
            };
            return allColumns.concat(column);
        }, []);
        const columnsWithChildrenColumn = [childrenColumn].concat(dayAttendanceColumns);
        return columnsWithChildrenColumn.concat(summaryColumns);
    }, [monthlyAttendanceData?.freeDays, selectedDate]);

    return (
        <>
            <FiltersContainer>
                <JournalAttendanceFilters
                    attendanceType="MONTHLY"
                    date={dateFilterValue ? parseStringIntoDayjsDate(dateFilterValue) : dayjs()}
                    firstSchoolYear={firstAndLastJournalSchoolYears?.firstYear}
                    lastSchoolYear={firstAndLastJournalSchoolYears?.lastYear}
                    isDatePickerDisabled={isJournalDataFetching}
                    onDateChange={handleChangeDateFilter}
                    onAttendanceTypeChange={handleAttendanceTypeChange}
                />
            </FiltersContainer>
            <MonthlyAttendanceTableHeader
                fullMonth={selectedFullMonth}
                year={selectedYear}
                totalPresences={monthlyAttendanceData?.presences || EMPTY_VALUE_LABEL}
                totalAbsences={monthlyAttendanceData?.absences || EMPTY_VALUE_LABEL}
            />
            <StyledTable
                emptyInfo={commonTexts.emptyInfo.noChildren}
                columns={columns}
                dataSource={monthlyAttendanceData?.childrenAttendance}
                rowKey={({ child }) => child.id}
                scroll={{ x: MONTHLY_ATTENDANCE_TABLE_WIDTH }}
                isTableHeaderHidden
                isTableManageUtilsHidden
                isTableColumnsTitlesVisibleOnMobile
                isError={isMonthlyAttendanceDataError || isJournalDataError}
                isDataInitialLoading={isMonthlyAttendanceDataInitialLoading}
                isDataRefetching={isMonthlyAttendanceDataRefetching}
                expandable={{ showExpandColumn: false }}
            />
        </>
    );
};
